import React, { useState } from 'react';
import { Button, Text } from '@fluentui/react-components';
import {
  ChevronDown20Regular,
  ChevronRight20Regular,
  Globe20Regular,
  Open16Regular,
} from '@fluentui/react-icons';
import { Box, useFluentTokens } from '../../../../theme/fluent';
import type { ApiSourceItem } from '../../types/api';

interface SourcesPanelProps {
  sources: ApiSourceItem[];
}

export const SourcesPanel: React.FC<SourcesPanelProps> = ({ sources }) => {
  const { tokens, modeTokens } = useFluentTokens();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!sources || sources.length === 0) {
    return null;
  }

  const handleOpenSource = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        borderTop: `1px solid ${modeTokens.colorNeutralStroke2}`,
        paddingTop: tokens.spacingVerticalS,
      }}
    >
      {/* Header toggle */}
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
        aria-expanded={isExpanded}
        aria-label={`Sources (${sources.length})`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.spacingHorizontalXS,
          cursor: 'pointer',
          padding: `${tokens.spacingVerticalXS} 0`,
        }}
      >
        {isExpanded ? (
          <ChevronDown20Regular style={{ color: modeTokens.colorNeutralForeground2 }} />
        ) : (
          <ChevronRight20Regular style={{ color: modeTokens.colorNeutralForeground2 }} />
        )}
        <Text
          size={300}
          weight="semibold"
          style={{ color: modeTokens.colorNeutralForeground1 }}
        >
          Sources
        </Text>
        <Text
          size={200}
          style={{ color: modeTokens.colorNeutralForeground3 }}
        >
          ({sources.length})
        </Text>
      </Box>

      {/* Source list */}
      {isExpanded && (
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginTop: tokens.spacingVerticalXS,
          }}
        >
          {sources.map((source) => (
            <SourceItem
              key={source.id}
              source={source}
              onOpen={handleOpenSource}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

interface SourceItemProps {
  source: ApiSourceItem;
  onOpen: (url: string) => void;
}

const SourceItem: React.FC<SourceItemProps> = ({ source, onOpen }) => {
  const { tokens, modeTokens } = useFluentTokens();

  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
        borderBottom: `1px solid ${modeTokens.colorNeutralStroke2}`,
      }}
    >
      {/* Favicon */}
      <Box
        style={{
          width: '32px',
          height: '32px',
          minWidth: '32px',
          borderRadius: tokens.borderRadiusMedium,
          backgroundColor: modeTokens.colorNeutralBackground3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {source.favicon_url ? (
          <img
            src={source.favicon_url}
            alt=""
            width={20}
            height={20}
            style={{ objectFit: 'contain' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement!.querySelector('svg')?.removeAttribute('style');
            }}
          />
        ) : null}
        <Globe20Regular
          style={{
            color: modeTokens.colorNeutralForeground3,
            ...(source.favicon_url ? { display: 'none' } : {}),
          }}
        />
      </Box>

      {/* Title & description */}
      <Box
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Text
          size={300}
          weight="regular"
          style={{
            color: modeTokens.colorNeutralForeground1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {source.title}
        </Text>
        {source.description && (
          <Text
            size={200}
            style={{
              color: modeTokens.colorNeutralForeground3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {source.description}
          </Text>
        )}
      </Box>

      {/* Open button */}
      <Button
        appearance="subtle"
        size="small"
        icon={<Open16Regular />}
        aria-label={`Open source: ${source.title}`}
        onClick={() => onOpen(source.url)}
        style={{ minWidth: 'auto' }}
      />
    </Box>
  );
};
